<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-clock-history me-2"></i>Shift Management
      </h4>
      <div class="d-flex gap-2">
        <button 
          v-if="!activeShift" 
          class="btn btn-primary" 
          @click="openStartShiftModal">
          <i class="bi bi-play-circle me-1"></i> Start Shift
        </button>
        <button 
          v-if="activeShift" 
          class="btn btn-success" 
          @click="viewShiftDetails(activeShift.id)">
          <i class="bi bi-eye me-1"></i> View Active Shift
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading shifts...</p>
      </div>

      <div v-if="!loading" class="shifts-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ shifts.total }}</span>
            <span class="text-muted">Shifts</span>
          </div>
          <div class="d-flex gap-2 align-items-center flex-wrap">
            <select class="form-select w-auto" v-model="filterStoreId" @change="onFilterChange">
              <option value="">All Stores</option>
              <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.name }}</option>
            </select>
            <select class="form-select w-auto" v-model="filterUserId" @change="onFilterChange">
              <option value="">All Users</option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.first_name }} {{ user.last_name }}</option>
            </select>
            <select class="form-select w-auto" v-model="filterStatus" @change="onFilterChange">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search shifts" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>REFERENCE</th>
                  <th>STORE</th>
                  <th>USER</th>
                  <th>OPENING CASH</th>
                  <th>CLOSING CASH</th>
                  <th>VARIANCE</th>
                  <th>STATUS</th>
                  <th>STARTED AT</th>
                  <th>ENDED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(shift, index) in shifts.data" :key="shift.id" class="shift-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ shift.reference }}</td>
                  <td>{{ shift.store?.name }}</td>
                  <td>{{ shift.user?.first_name }} {{ shift.user?.last_name }}</td>
                  <td>{{ formatNumber(shift.opening_cash) }}</td>
                  <td>{{ shift.closing_cash ? formatNumber(shift.closing_cash) : '-' }}</td>
                  <td>
                    <span v-if="shift.cash_difference" :class="getCashDifferenceClass(shift.cash_difference)">
                      {{ 'KES ' + shift.cash_difference }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <Badge pill :type="shift.status === 'active' ? 'success' : 'secondary'">
                      {{ shift.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(shift.started_at) }}</td>
                  <td>{{ shift.ended_at ? formatDate(shift.ended_at) : '-' }}</td>
                  <td>
                    <button class="btn btn-info btn-sm" @click="viewShiftDetails(shift.id)">
                      <i class="bi bi-eye me-1"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <Modal :open="startShiftModal" @closed="startShiftModal = false">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-play-circle me-2 text-primary"></i>Start New Shift
        </h5>
        <button @click="startShiftModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="startShift">
          <div class="row">
            <div class="form-group col-md-12 mb-3">
              <label for="opening_cash" class="form-label fw-medium mb-2">Opening Cash Amount:</label>
              <input type="number" step="0.01" class="form-control" v-model="newShift.opening_cash" required>
            </div>
            <div class="form-group col-md-12 mb-3">
              <label for="notes" class="form-label fw-medium mb-2">Notes (Optional):</label>
              <textarea class="form-control" v-model="newShift.notes" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="startShiftModal = false">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <i class="bi bi-play-circle me-1"></i> Start Shift
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </Card>
</template>

<script>
import shiftService from "@/api/shiftService"
import storeService from "@/api/storeService"
import userService from "@/api/userService"
import router from "@/router"
import {decrypt} from "@/utils/crypto"
import formatDate from "@/utils/formatDate"
import formatNumber from "@/utils/formatNumber"

export default {
  name: "Shifts",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      profile: {},
      loading: true,
      shifts: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      filterStatus: this.$route?.query?.status ?? '',
      filterStoreId: this.$route?.query?.store_id ?? '',
      filterUserId: this.$route?.query?.user_id ?? '',
      stores: [],
      users: [],
      startShiftModal: false,
      newShift: {
        store_id: this.store.id,
        opening_cash: 0,
        notes: ''
      },
    }
  },

  methods:{
    formatDate,
    formatNumber,

    async fetchShifts() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch,
        status: this.filterStatus,
        store_id: this.filterStoreId,
        user_id: this.filterUserId
      }
      this.shifts = await shiftService.getShifts(params)
      this.loading = false
    },

    async fetchStores() {
      this.stores = await storeService.getStores()
    },

    async fetchUsers() {
      const response = await userService.getUsers({ per_page: 100 })
      this.users = response.data || []
    },

    onSearchClick() {
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchShifts()
    },

    onClearSearchClick() {
      this.dataTableSearch = ''
      this.filterStatus = ''
      this.filterStoreId = ''
      this.filterUserId = ''
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchShifts()
    },

    onFilterChange() {
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchShifts()
    },

    updateQueryParams() {
      const query = {}
      if (this.dataTablePage > 1) query.page = this.dataTablePage
      if (this.dataTablePerPage !== 10) query.per_page = this.dataTablePerPage
      if (this.dataTableSearch) query.search = this.dataTableSearch
      if (this.filterStatus) query.status = this.filterStatus
      if (this.filterStoreId) query.store_id = this.filterStoreId
      if (this.filterUserId) query.user_id = this.filterUserId
      router.push({ query })
    },

    openStartShiftModal() {
      this.newShift = {
        store_id: this.store.id,
        opening_cash: 0,
        notes: ''
      }
      this.startShiftModal = true
    },

    async startShift() {
      const shiftData = {
        store_id: this.store.id,
        opening_cash: this.newShift.opening_cash,
        notes: this.newShift.notes
      }
      const shift = await shiftService.startShift(shiftData)
      if (shift) {
        this.startShiftModal = false
        await this.fetchShifts()
      }
    },

    viewShiftDetails(id) {
      router.push('/shift/' + id)
    },

    getCashDifferenceClass(difference) {
      const diff = parseFloat(difference)
      if (diff > 0) return 'text-success fw-bold'
      if (diff < 0) return 'text-danger fw-bold'
      return 'text-muted'
    },
  },

  computed: {
    totalPages() {
      return Math.ceil(this.shifts.total / this.dataTablePerPage)
    }
  },

  watch: {
    dataTablePage() {
      this.updateQueryParams()
      this.fetchShifts()
    },
    dataTablePerPage() {
      this.dataTablePage = 1
      this.updateQueryParams()
      this.fetchShifts()
    }
  },

  async mounted() {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.fetchStores()
    await this.fetchUsers()
    await this.fetchShifts()
  }
}
</script>

<style scoped>
.shifts-content {
  padding: 0.5rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.shift-row {
  transition: all 0.2s ease;
}

.shift-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
